import { z } from 'zod'

/**
 * Money: integer cents, always (CLAUDE.md rule 4).
 *
 * `Cents` is a branded integer. Nothing produces one except `cents()` (which
 * refuses fractions and unsafe magnitudes) and the arithmetic below, so a
 * float can never be passed where an amount is expected without a cast that
 * stands out in review. There is no `toFixed` anywhere in this file: every
 * division is explicit about which way it rounds, and splits distribute the
 * remainder so the parts always add back to the whole.
 */

declare const centsBrand: unique symbol
export type Cents = number & { readonly [centsBrand]: true }

/** Largest amount the platform handles: $100,000,000.00. Far past any gift. */
export const MAX_CENTS = 10_000_000_000

export function cents(value: number): Cents {
  if (!Number.isSafeInteger(value)) throw new RangeError(`Not a whole number of cents: ${value}`)
  if (Math.abs(value) > MAX_CENTS) throw new RangeError(`Amount out of range: ${value}`)
  return value as Cents
}

export const ZERO = cents(0)

export const isCents = (value: unknown): value is Cents =>
  typeof value === 'number' && Number.isSafeInteger(value) && Math.abs(value) <= MAX_CENTS

export const add = (a: Cents, b: Cents): Cents => cents(a + b)
export const sub = (a: Cents, b: Cents): Cents => cents(a - b)
export const negate = (a: Cents): Cents => cents(-a)
export const sum = (values: readonly Cents[]): Cents => values.reduce<Cents>(add, ZERO)

/**
 * A rate in basis points (1/100 of a percent), so fee schedules are integers
 * too: 2.9% is 290 bps. Multiplying cents by a rate is the one place money
 * meets a fraction, and it rounds half away from zero, once, at the end.
 */
export function applyRate(amount: Cents, basisPoints: number): Cents {
  if (!Number.isSafeInteger(basisPoints) || basisPoints < 0) {
    throw new RangeError(`Not a rate in basis points: ${basisPoints}`)
  }
  const product = amount * basisPoints
  const rounded = Math.sign(product) * Math.floor((Math.abs(product) + 5_000) / 10_000)
  return cents(rounded)
}

// ---- parsing and formatting -----------------------------------------------------

/**
 * "12.5", "$1,200.00", "0.99" → cents. Parsed as text, digit by digit — never
 * through `parseFloat`, which would turn "0.29" into 28.999999999999996 cents
 * the moment anyone multiplied it. Returns null for anything that is not a
 * plain amount with at most two decimals.
 */
export function parseCents(input: string): Cents | null {
  const cleaned = input.trim().replace(/[$,\s]/g, '')
  const match = /^(-)?(\d+)(?:\.(\d{1,2}))?$/.exec(cleaned)
  if (!match) return null
  const [, minus, whole = '0', fraction = ''] = match
  const value = Number(whole) * 100 + Number(fraction.padEnd(2, '0'))
  if (!Number.isSafeInteger(value) || value > MAX_CENTS) return null
  return cents(minus ? -value : value)
}

/** "1234.56" — for CSV and machine-readable output. Integer arithmetic only. */
export function toDecimalString(amount: Cents): string {
  const abs = Math.abs(amount)
  return `${amount < 0 ? '-' : ''}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`
}

export function formatCents(amount: Cents, currency = 'USD', locale = 'en-US'): string {
  // Integer division into units and cents, so formatting never sees a float
  // it might mis-round; Intl only decorates the result.
  const negative = amount < 0
  const abs = Math.abs(amount)
  const units = Math.floor(abs / 100)
  const rest = abs % 100
  const formatted = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(`${units}.${String(rest).padStart(2, '0')}`))
  return negative ? `-${formatted}` : formatted
}

// ---- splitting ------------------------------------------------------------------

/**
 * Splits `total` across `weights` in proportion, by the largest-remainder
 * method: every part is floored, then the leftover cents go one at a time to
 * the parts whose fractions were largest (ties to the earlier part). The parts
 * always sum to the total exactly — the property a multi-fund gift and a
 * proportional fee split both depend on.
 */
export function allocate(total: Cents, weights: readonly number[]): Cents[] {
  if (weights.length === 0) throw new RangeError('Nothing to allocate across')
  if (weights.some((weight) => !Number.isSafeInteger(weight) || weight < 0)) {
    throw new RangeError('Weights must be non-negative integers')
  }
  const totalWeight = weights.reduce((acc, weight) => acc + weight, 0)
  if (totalWeight === 0) throw new RangeError('Weights must not all be zero')

  const sign = total < 0 ? -1 : 1
  const abs = Math.abs(total)
  const exact = weights.map((weight) => (abs * weight) / totalWeight)
  const floors = exact.map((value) => Math.floor(value))
  let leftover = abs - floors.reduce((acc, value) => acc + value, 0)
  const order = exact
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    .sort((a, b) => b.fraction - a.fraction || a.index - b.index)
  for (const { index } of order) {
    if (leftover === 0) break
    floors[index] = (floors[index] ?? 0) + 1
    leftover -= 1
  }
  return floors.map((value) => cents(sign * value))
}

// ---- processing fees ----------------------------------------------------------------

export type PaymentKind = 'card' | 'ach'

export interface FeeSchedule {
  /** Percentage part, in basis points. */
  rateBps: number
  /** Fixed part per transaction. */
  fixed: Cents
  /** Cap on the total fee, if the processor has one (ACH does). */
  cap: Cents | null
}

/**
 * Stripe's standard US pricing — the default until a church's own rates are
 * configured. Card: 2.9% + 30¢. ACH Direct Debit: 0.8%, capped at $5.
 */
export const DEFAULT_FEES: Record<PaymentKind, FeeSchedule> = {
  card: { rateBps: 290, fixed: cents(30), cap: null },
  ach: { rateBps: 80, fixed: cents(0), cap: cents(500) },
}

/** The processor's fee on a gross amount. */
export function feeOn(gross: Cents, schedule: FeeSchedule): Cents {
  if (gross <= 0) return ZERO
  const fee = add(applyRate(gross, schedule.rateBps), schedule.fixed)
  return schedule.cap !== null && fee > schedule.cap ? schedule.cap : fee
}

/**
 * "Cover the fees": the gross to charge so that, after the processor's fee,
 * the church receives at least `net`.
 *
 * Solved by search rather than by formula, because the processor rounds and
 * caps and a closed form that ignores either is off by a cent in some band of
 * amounts. Starts from the algebraic estimate and steps the last few cents, so
 * the answer is the *smallest* gross whose net is at least the gift.
 */
export function grossUpForFees(net: Cents, schedule: FeeSchedule): Cents {
  if (net <= 0) return ZERO
  const estimate = Math.floor(((net + schedule.fixed) * 10_000) / (10_000 - schedule.rateBps))
  let gross = cents(Math.max(net, estimate - 2))
  while (sub(gross, feeOn(gross, schedule)) < net) gross = add(gross, cents(1))
  // Step back while one cent less would still cover it (the estimate can overshoot).
  while (gross > net && sub(sub(gross, cents(1)), feeOn(sub(gross, cents(1)), schedule)) >= net) {
    gross = sub(gross, cents(1))
  }
  return gross
}

// ---- schemas ------------------------------------------------------------------------

/** Cents on the wire: a positive whole number within range. */
export const centsSchema = z
  .number()
  .int('Amounts are whole cents')
  .min(1, 'Must be more than zero')
  .max(MAX_CENTS)
  .transform((value) => cents(value))
